import type { FC, ReactNode } from 'react'
import type { MediaItem, TravelOption } from '@/types'

import Image from 'next/image'
import { useRef } from 'react'

import { onPageReveal } from '@/lib/transition'
import { TransitionLink } from '@/components/generals/transition-link'
import { useSmoothScroll } from '@/components/generals/smooth-scroll'

import {
    FULL_MOTION,
    gsap,
    ScrollTrigger,
    SplitText,
    useGSAP
} from '@/lib/gsap'

interface PageHeroProps {
    index: string
    eyebrow: string
    title: string
    accent: string
    image: MediaItem
    facts: TravelOption[]
    /** Id of the first section below, for the scroll cue. */
    next: string
}

/** Full-screen cinematic opening for every page after the home page. */
export const PageHero: FC<PageHeroProps> = ({
    index,
    eyebrow,
    title,
    accent,
    image,
    facts,
    next
}): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const { scrollTo } = useSmoothScroll()

    useGSAP(() => {
        const section = sectionRef.current
        const heading = section?.querySelector<HTMLElement>('[data-ph-title]')
        if (!section || !heading) return

        const mm = gsap.matchMedia()

        mm.add(FULL_MOTION, () => {
            const drift = section.querySelector('[data-ph-drift]')
            const zoom = section.querySelector('[data-ph-zoom]')
            const scroller = section.querySelector('[data-ph-scroll]')
            const content = section.querySelector('[data-ph-content]')
            const shade = section.querySelector('[data-ph-shade]')
            const fades = section.querySelectorAll('[data-ph-fade]')

            const split = SplitText.create(heading, { type: 'lines', mask: 'lines', linesClass: 'split-line' })

            gsap.set(split.lines, { yPercent: 115 })
            gsap.set(fades, { autoAlpha: 0, y: 18 })
            gsap.set(zoom, { scale: 1.22 })

            let entrance: gsap.core.Timeline | undefined

            const unsubscribe = onPageReveal(() => {
                entrance = gsap.timeline()
                    .to(zoom, { scale: 1, duration: 2.6, ease: 'expo.out' }, 0)
                    .to(split.lines, { yPercent: 0, duration: 1.5, ease: 'expo.out', stagger: 0.1 }, 0.2)
                    .to(fades, { autoAlpha: 1, y: 0, duration: 1.2, ease: 'power3.out', stagger: 0.08 }, 0.6)
            })

            const loop = gsap.fromTo(drift, { scale: 1.02, xPercent: 0 }, {
                scale: 1.1,
                xPercent: -2,
                duration: 24,
                ease: 'sine.inOut',
                repeat: -1,
                yoyo: true
            })

            ScrollTrigger.create({
                trigger: section,
                start: 'top top',
                end: '+=100%',
                pin: true,
                pinSpacing: false,
                onLeave: () => loop.pause(),
                onEnterBack: () => loop.resume()
            })

            gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: '+=100%',
                    scrub: true
                }
            })
                .to(scroller, { scale: 1.14, yPercent: 6, ease: 'none', duration: 1 }, 0)
                .to(content, { yPercent: -14, ease: 'none', duration: 1 }, 0)
                .to(content, { autoAlpha: 0, ease: 'none', duration: 0.55 }, 0.2)
                .to(shade, { opacity: 0.7, ease: 'none', duration: 1 }, 0)

            return () => {
                unsubscribe()
                entrance?.kill()
                split.revert()
            }
        })

        return () => mm.revert()
    }, { scope: sectionRef })

    return (
        <section
            ref={sectionRef}
            id='page-hero'
            aria-labelledby='page-title'
            className='relative h-[100svh] min-h-[600px] w-full overflow-hidden bg-pine text-bone'
        >
            <div data-ph-scroll className='absolute inset-0 will-change-transform'>
                <div data-ph-zoom className='absolute inset-0'>
                    <div data-ph-drift className='absolute inset-0'>
                        <Image
                            src={image.src}
                            alt={image.alt}
                            fill
                            preload
                            sizes='100vw'
                            className='object-cover'
                            style={{ objectPosition: image.position }}
                        />
                    </div>
                </div>
            </div>

            <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-pine/70 to-transparent' />
            <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 bottom-0 h-[70%] bg-gradient-to-t from-pine via-pine/45 to-transparent' />
            <div aria-hidden='true' className='pointer-events-none absolute -left-1/4 top-[42%] h-[36%] w-[150%] animate-mist bg-[radial-gradient(50%_50%_at_50%_50%,rgba(242,238,230,0.14),transparent_70%)]' />
            <div aria-hidden='true' data-ph-shade className='pointer-events-none absolute inset-0 bg-pine opacity-0' />

            <div aria-hidden='true' className='pointer-events-none absolute inset-0 overflow-hidden'>
                <div className='grain animate-grain' />
            </div>

            <div data-ph-content className='relative z-10 flex h-full flex-col justify-between px-5 pb-8 pt-24 sm:px-8 lg:px-12 lg:pb-10 lg:pt-28'>
                <nav aria-label='Breadcrumb' data-ph-fade>
                    <ol className='flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.24em] text-bone/75 sm:text-[11px]'>
                        <li>
                            <TransitionLink href='/' className='transition-colors hover:text-bone'>
                                Morrow
                            </TransitionLink>
                        </li>

                        <li aria-hidden='true'>
                            /
                        </li>

                        <li aria-current='page' className='text-bone'>
                            {eyebrow}
                        </li>
                    </ol>
                </nav>

                <div>
                    <p data-ph-fade className='mb-5 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.24em] text-bone/75'>
                        <span className='tabular-nums'>
                            {index}
                        </span>

                        <span aria-hidden='true' className='h-px w-8 bg-current opacity-50' />

                        <span>
                            {eyebrow}
                        </span>
                    </p>

                    <h1
                        id='page-title'
                        data-ph-title
                        className='type-wide max-w-[16ch] text-[clamp(2.6rem,8.4vw,8.75rem)] font-medium uppercase leading-[0.9] tracking-[-0.03em]'
                    >
                        {title}
                    </h1>

                    <div className='mt-7 flex flex-col gap-6 border-t border-bone/20 pt-5 lg:mt-9 lg:flex-row lg:items-end lg:justify-between'>
                        <div className='flex items-end gap-6'>
                            <button
                                type='button'
                                data-ph-fade
                                onClick={() => scrollTo(`#${next}`)}
                                aria-label='Scroll to the next section'
                                className='group/cue hidden items-center sm:flex'
                            >
                                <span aria-hidden='true' className='relative block h-10 w-px overflow-hidden bg-bone/25'>
                                    <span className='absolute inset-x-0 top-0 block h-1/2 animate-scroll-cue bg-bone' />
                                </span>
                            </button>

                            <p data-ph-fade className='max-w-xl font-serif text-[clamp(1.35rem,2.2vw,2rem)] italic leading-snug text-bone/90'>
                                {accent}
                            </p>
                        </div>

                        <dl data-ph-fade className='grid grid-cols-3 gap-5 sm:gap-8 lg:shrink-0'>
                            {facts.map((fact) => (
                                <div key={fact.label} className='flex flex-col-reverse gap-1.5'>
                                    <dt className='font-mono text-[9px] uppercase tracking-[0.22em] text-bone/60 sm:text-[10px]'>
                                        {fact.label}
                                    </dt>

                                    <dd className='type-wide text-lg font-medium leading-none sm:text-2xl'>
                                        {fact.value}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </div>
            </div>
        </section>
    )
}
